import React, { useState } from "react";

import styles from "./PaigeReview.module.scss";

/**
 * Renders the page insights section for Illumina analyses.
 * Groups insights by page number and shows detected products with
 * their classification (RUO/IVD) and detection method.
 * Unresolved insights (entity_name: null, detection_method: "unresolved")
 * are surfaced as items needing manual review.
 *
 * @param {Object} props
 * @param {Array} props.pageInsights - flat list of page insight objects
 */
var PageInsightsSection = function (props) {
  var pageInsights = props.pageInsights || [];

  var _expanded = useState(true);
  var isExpanded = _expanded[0];
  var setIsExpanded = _expanded[1];

  if (pageInsights.length === 0) return null;

  // Group insights by page number
  var insightsByPage = {};
  pageInsights.forEach(function (insight) {
    var page = insight.page_number || 0;
    if (!insightsByPage[page]) {
      insightsByPage[page] = [];
    }
    insightsByPage[page].push(insight);
  });

  // Sort page numbers
  var pageNumbers = Object.keys(insightsByPage)
    .map(function (p) {
      return parseInt(p, 10);
    })
    .sort(function (a, b) {
      return a - b;
    });

  // Count unresolved insights
  var unresolvedCount = pageInsights.filter(function (insight) {
    return insight.detection_method === "unresolved";
  }).length;

  var toggleExpanded = function () {
    setIsExpanded(!isExpanded);
  };

  /**
   * Returns a human-readable label for the entity classification
   * @param {string} classification - e.g. "ruo", "ivd"
   * @returns {string}
   */
  var formatClassification = function (classification) {
    if (!classification) return "";
    return classification.toUpperCase();
  };

  /**
   * Returns a human-readable label for the detection method
   * @param {string} method - e.g. "text_scan", "unresolved"
   * @returns {string}
   */
  var formatDetectionMethod = function (method) {
    if (!method) return "";
    if (method === "text_scan") return "Text Scan";
    if (method === "unresolved") return "Unresolved";
    // Replace underscores with spaces and capitalize
    return method
      .split("_")
      .map(function (word) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");
  };

  var isUnresolved = function (insight) {
    return insight.detection_method === "unresolved";
  };

  return (
    <div className={styles.pageInsightsSection}>
      <div className={styles.pageInsightsHeader} onClick={toggleExpanded}>
        <div className={styles.pageInsightsTitle}>
          Product Detection
          <span className={styles.pageInsightsCount}>
            {pageInsights.length}
          </span>
          {unresolvedCount > 0 && (
            <span className={styles.unresolvedBadge}>
              {unresolvedCount} needs review
            </span>
          )}
        </div>
        <span className={styles.expandToggle}>
          {isExpanded ? "\u25B2" : "\u25BC"}
        </span>
      </div>

      {isExpanded && (
        <div className={styles.pageInsightsBody}>
          {pageNumbers.map(function (pageNum) {
            var insights = insightsByPage[pageNum];
            return (
              <div key={pageNum} className={styles.pageInsightsPage}>
                <div className={styles.pageInsightsPageHeader}>
                  Page {pageNum}
                </div>
                <div className={styles.pageInsightsEntities}>
                  {insights.map(function (insight) {
                    var unresolved = isUnresolved(insight);
                    var entityClass = unresolved
                      ? styles.insightEntity + " " + styles.insightUnresolved
                      : styles.insightEntity;

                    return (
                      <div key={insight.id} className={entityClass}>
                        <div className={styles.insightEntityTop}>
                          <span className={styles.insightEntityName}>
                            {unresolved
                              ? "Unknown Entity"
                              : insight.entity_name}
                          </span>
                          {insight.entity_classification && (
                            <span
                              className={
                                styles.classificationBadge +
                                " " +
                                (insight.entity_classification === "ivd"
                                  ? styles.classificationIvd
                                  : styles.classificationRuo)
                              }
                            >
                              {formatClassification(
                                insight.entity_classification
                              )}
                            </span>
                          )}
                        </div>
                        <div className={styles.insightEntityMeta}>
                          {insight.entity_type && (
                            <span className={styles.insightEntityType}>
                              {insight.entity_type}
                            </span>
                          )}
                          <span className={styles.insightDetectionMethod}>
                            {formatDetectionMethod(insight.detection_method)}
                          </span>
                          {insight.confidence && !unresolved && (
                            <span
                              className={
                                styles.insightConfidence +
                                " " +
                                (insight.confidence === "high"
                                  ? styles.confidenceHigh
                                  : insight.confidence === "medium"
                                  ? styles.confidenceMedium
                                  : styles.confidenceLow)
                              }
                            >
                              {insight.confidence}
                            </span>
                          )}
                        </div>
                        {unresolved && (
                          <div className={styles.unresolvedNote}>
                            Manual review required
                          </div>
                        )}
                        {insight.metadata &&
                          insight.metadata.matched_term &&
                          !unresolved && (
                            <div className={styles.insightMatchedTerm}>
                              Matched: {insight.metadata.matched_term}
                            </div>
                          )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PageInsightsSection;
